import { useCallback } from 'react';
import { UseMutationResult } from 'react-query';

import { useFormik } from 'formik';
import { ArrowLeft2 } from 'iconsax-react';

import { DialogHasntNumber } from '@/domain/address/components/Dialog/HasntNumber';
import { MapAddress } from '@/domain/address/components/Map';
import { Address } from '@/domain/address/models/address';
import { getFormattedAddress } from '@/domain/address/utils';
import { setAddressOnEditStep } from '@/domain/address/validations';
import { useAddressContext } from '@/shared/contexts/AddressContext';
import { executeEvent } from '@/shared/events/Firebase/firebaseEvents';
import { useDisclosure } from '@/shared/hooks/useDisclosure';
import { animationLoadOptions } from '@/shared/utils/loading';
import { unmaskObject } from '@/shared/utils/regex';
import { Button, Input, Text } from '@develop-fapp/ui-kit-fapp';

import { Form, Wrapper } from './styles';

interface EditAddressProps {
  addressConfirmationMutation: UseMutationResult<
    Address[],
    unknown,
    Address,
    unknown
  >;
}

export const EditAddress: React.FC<EditAddressProps> = ({
  addressConfirmationMutation,
}) => {
  const {
    currentAddressId,
    geoLocation,
    mapRef,
    setGeoLocation,
    setMapRef,
    setCurrentStep,
    selectAddressToEdit,
  } = useAddressContext();

  const dialogHasntNumber = useDisclosure();

  const addressWithNumber =
    selectAddressToEdit?.number === '0' ? '' : selectAddressToEdit?.number;

  const formik = useFormik({
    initialValues: {
      title: selectAddressToEdit?.title ?? '',
      street: selectAddressToEdit?.street ?? '',
      number: addressWithNumber ?? '',
      complement: selectAddressToEdit?.complement ?? '',
      neighborhood: selectAddressToEdit?.neighborhood ?? '',
      referencePoint: selectAddressToEdit?.referencePoint ?? '',
      postalCode: selectAddressToEdit?.postalCode ?? '',
    },
    validationSchema: setAddressOnEditStep,
    onSubmit: values => {
      if (!values.number) {
        dialogHasntNumber.onOpen();
        return;
      }

      executeEvent('click_save_address', {});
      addressConfirmationMutation.mutate(unmaskObject(values) as Address);
    },
  });

  const handleOnGoBack = useCallback(() => {
    setCurrentStep(currentAddressId ? 'create' : 'search');
  }, [setCurrentStep, currentAddressId]);

  return (
    <>
      <Wrapper>
        <header>
          <ArrowLeft2 onClick={handleOnGoBack} size={24} variant="Outline" />
          <Text variant="h7">
            {getFormattedAddress(
              selectAddressToEdit?.street ?? '',
              selectAddressToEdit?.neighborhood ?? '',
              selectAddressToEdit?.number ?? '',
              selectAddressToEdit?.city ?? '',
              selectAddressToEdit?.state ?? '',
            )}
          </Text>
        </header>

        <main>
          {(geoLocation.latitude !== null ||
            geoLocation.longitude !== null) && (
            <MapAddress
              geoLocation={geoLocation}
              setGeoLocation={setGeoLocation}
              mapRef={mapRef}
              setMapRef={setMapRef}
              height="100px"
            />
          )}

          <Form>
            <div>
              <section>
                <Input
                  name="postalCode"
                  placeholder="Cep"
                  mask="cep"
                  value={formik.values.postalCode}
                  onChange={formik.handleChange}
                  errorMessage={
                    formik.touched.postalCode ? formik.errors.postalCode : ''
                  }
                  inputMode="numeric"
                  maxLength={9}
                />
                <Input
                  name="neighborhood"
                  placeholder="Bairro"
                  value={formik.values.neighborhood}
                  onChange={formik.handleChange}
                  errorMessage={
                    formik.touched.neighborhood
                      ? formik.errors.neighborhood
                      : ''
                  }
                  readOnly={!!selectAddressToEdit?.neighborhood}
                />
              </section>
              <Input
                name="street"
                placeholder="Rua"
                value={formik.values.street}
                onChange={formik.handleChange}
                errorMessage={formik.touched.street ? formik.errors.street : ''}
                readOnly={!!selectAddressToEdit?.street}
              />
              <section>
                <Input
                  name="number"
                  placeholder="Número"
                  value={formik.values.number}
                  onChange={formik.handleChange}
                  errorMessage={
                    formik.touched.number ? formik.errors.number : ''
                  }
                  maxLength={5}
                  inputMode="numeric"
                />
                <Input
                  name="complement"
                  placeholder="Complemento"
                  value={formik.values.complement}
                  onChange={formik.handleChange}
                  maxLength={25}
                />
              </section>
              <Input
                name="referencePoint"
                placeholder="Ponto de referência"
                value={formik.values.referencePoint}
                onChange={formik.handleChange}
                maxLength={40}
              />
              <Input
                name="title"
                placeholder="Apelido"
                value={formik.values.title}
                onChange={formik.handleChange}
                maxLength={20}
              />
            </div>
          </Form>
        </main>

        <footer>
          <Button
            type="button"
            id="btn-confirm-address"
            variant="contained"
            color="primary"
            onClick={() => formik.handleSubmit()}
            disabled={addressConfirmationMutation?.isLoading || !formik.isValid}
            animationLoadOptions={animationLoadOptions(
              addressConfirmationMutation?.isLoading,
              addressConfirmationMutation?.isSuccess,
              addressConfirmationMutation?.isError,
            )}
          >
            Confirmar endereço
          </Button>
        </footer>
      </Wrapper>

      <DialogHasntNumber
        isOpen={dialogHasntNumber.isOpen}
        onClose={dialogHasntNumber.onClose}
        onConfirm={() => {
          formik.setValues({
            ...formik.values,
            number: '0',
          });
          dialogHasntNumber.onClose();
          formik.handleSubmit();
        }}
      />
    </>
  );
};
